import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { removeInternship } from '@/api/adminRecruitment.api';
import { Alert, Button, Icon, Modal, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

const MIN_REASON = 10;

/**
 * "Remove as fraudulent": the stronger sibling of take-down. The listing is
 * archived for good, every open application on it is closed, and the company
 * and each applicant are told. `internship` is `{ id, title }` or null.
 */
export function RemoveListingModal({ internship, onClose, onRemoved }) {
  const toast = useToast();
  const [reason, setReason] = useState('');

  const remove = useMutation({
    mutationFn: ({ id, text }) => removeInternship(id, text),
    onSuccess: (result) => {
      const closed = result?.applications_closed ?? 0;
      toast.success(
        closed > 0
          ? `Listing removed; ${closed} application${closed === 1 ? '' : 's'} closed`
          : 'Listing removed',
      );
      setReason('');
      onRemoved?.(result);
      onClose();
    },
    onError: (removeError) =>
      toast.error(errorMessage(removeError, 'Could not remove the listing')),
  });

  const text = reason.trim();

  return (
    <Modal
      open={Boolean(internship)}
      title={`Remove "${internship?.title}" as fraudulent`}
      description="Use this for scams and abuse. For a listing that only needs to stop taking applicants, take it down instead."
      onClose={onClose}
    >
      <div className="space-y-4">
        <Alert tone="error" title="This cannot be undone">
          The listing is hidden for good and the company cannot republish it. Every open application
          is closed, booked interviews are cancelled and open offers withdrawn. The company and
          every applicant are notified.
        </Alert>
        <Textarea
          label="Reason"
          required
          rows={4}
          hint="The company sees this. Applicants are only told the listing broke the rules."
          placeholder="The listing asks applicants to pay a registration fee before an interview."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={text.length < MIN_REASON}
            isLoading={remove.isPending}
            onClick={() => remove.mutate({ id: internship.id, text })}
          >
            <Icon name="flag" size={15} />
            Remove listing
          </Button>
        </div>
      </div>
    </Modal>
  );
}
